import { BehaviorSubject, delay, Observable } from "rxjs";
import { CollectionViewer, DataSource } from "@angular/cdk/collections";
import { FeedInRatesService } from "../../../../services/feed-in-rates.service";
import { FeedInRate, FeedInRateUiRow } from "../../../../model/feed-in-rate";

// Config: Define possible fields
const FIELD_DEFINITIONS = [
  { key: 'feedInRate', label: 'Feed-in Tariff (Flat rate)' },
  { key: 'kWpRoofLowerThan10', label: 'Rooftop < 10 kWp' },
  { key: 'kWpRoofBetween10And40', label: 'Rooftop 10-40 kWp' },
  { key: 'kWpRoofBetween40And100', label: 'Rooftop 40-100 kWp' },
  { key: 'kWpRoofGreaterThan100', label: 'Rooftop > 100 kWp' },
  { key: 'kWpOpenAreaLowerThan100', label: 'Open Area < 100 kWp' },
  { key: 'kWpOpenAreaGreaterThan100', label: 'Open Area > 100 kWp' }
];

export class FeedInRatesDataSource implements DataSource<FeedInRateUiRow> {

  private rawFeedInRate: FeedInRate | null = null;
  private removedKeys = new Set<string>();

  rowsSubject = new BehaviorSubject<FeedInRateUiRow[]>([]);

  loading$: Observable<boolean>;

  constructor(private service: FeedInRatesService) {
    this.loading$ = this.service.loading$.pipe(delay(10));
  }

  setData(data: FeedInRate, showAllFields: boolean = false) {
    this.rawFeedInRate = JSON.parse(JSON.stringify(data));
    this.removedKeys.clear();

    const rows = this.transformObjectToRows(data, showAllFields);
    this.rowsSubject.next(rows);
  }

  removeRow(key: string) {
    this.removedKeys.add(key);
    const current = this.rowsSubject.value.filter(r => r.key !== key);
    this.rowsSubject.next(current);
  }

  connect(): Observable<FeedInRateUiRow[]> {
    return this.rowsSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.rowsSubject.complete();
  }

  // --- Transformation Logic ---

  private transformObjectToRows(data: FeedInRate, showAllFields: boolean): FeedInRateUiRow[] {
    const rows: FeedInRateUiRow[] = [];

    FIELD_DEFINITIONS.forEach(def => {
      const val = (data as any)[def.key];

      const isValid = showAllFields || (val !== undefined && val !== null && val !== 0);

      if (isValid) {
        rows.push({
          key: def.key,
          label: def.label,
          value: val,
          originalValue: val,
          lastModifiedBy: data.lastModifiedBy,
          lastModifiedDate: data.lastModifiedDate
        });
      }
    });

    return rows;
  }

  // --- Change Tracking ---

  hasChanged(row: FeedInRateUiRow): boolean {
    return row.value !== row.originalValue;
  }

  resetRow(row: FeedInRateUiRow) {
    row.value = row.originalValue;
  }

  getUpdates(): FeedInRate | null {
    if (!this.rawFeedInRate) return null;

    const rows = this.rowsSubject.value;
    const hasChanges = rows.some(r => this.hasChanged(r)) || this.removedKeys.size > 0;

    if (!hasChanges) return null;

    // Copy of the original
    const updatedObject = JSON.parse(JSON.stringify(this.rawFeedInRate));

    rows.forEach(row => {
      (updatedObject as any)[row.key] = row.value;
    });

    // Null out removed rows
    this.removedKeys.forEach(key => {
      (updatedObject as any)[key] = null;
    });

    return updatedObject;
  }

  applyUpdate(updatedObject: FeedInRate) {
    this.setData(updatedObject);
  }
}
